import { Link } from 'react-router-dom'
import { orgPath } from '../../utils/org-slug'
import { SETUP_NAV_ITEMS, setupTitleForPath } from './setupNav'

interface SetupGuideShellProps {
  /** Current router pathname (e.g. from useLocation) — drives the "next guide" rail. */
  pathname: string
  /** Page title (h1). */
  title: string
  /** One-line subtitle under the title. */
  description: string
  children: React.ReactNode
}

/**
 * Common shell for every page under "Setup Instructions":
 * breadcrumb ("Setup Instructions / <Guide>"), h1 + description, then the
 * guided content, with a "next guide" footer rail for cross-guide discovery.
 */
export default function SetupGuideShell({
  pathname,
  title,
  description,
  children,
}: SetupGuideShellProps) {
  const normalized = pathname.split('?')[0]
  const currentIndex = SETUP_NAV_ITEMS.findIndex((item) => normalized.startsWith(item.to))
  const next =
    currentIndex >= 0 ? SETUP_NAV_ITEMS[(currentIndex + 1) % SETUP_NAV_ITEMS.length] : undefined
  const activeTitle = setupTitleForPath(normalized)

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <div>
        <nav aria-label="Breadcrumb">
          <ol className="flex items-center gap-1.5 text-sm text-harbor-text-muted">
            <li>Setup Instructions</li>
            <li aria-hidden="true">/</li>
            <li aria-current="page" className="font-medium text-harbor-text-secondary">
              {activeTitle ?? title}
            </li>
          </ol>
        </nav>
        <h1 className="mt-2 text-2xl font-bold text-harbor-text">{title}</h1>
        <p className="mt-1 text-harbor-text-secondary">{description}</p>
      </div>

      {children}

      {next && (
        <div className="rounded-xl border border-harbor-border bg-harbor-surface p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-harbor-text-muted">
            Next guide
          </p>
          <Link
            to={orgPath(next.to)}
            className="mt-1 inline-flex items-center gap-2 text-sm font-medium text-emerald-400 hover:text-emerald-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-harbor-ring-offset rounded"
          >
            {next.label} <span aria-hidden="true">→</span>
          </Link>
          <p className="mt-1 text-sm text-harbor-text-secondary">{next.blurb}</p>
        </div>
      )}
    </div>
  )
}
